<template>
  <div class="modal-wrapper">
    <q-form @submit.prevent="handleFormSubmit" class="form">
      <div class="form__item">
        <div v-if="this.loading" class="loading-overlay">
          <div class="loading-content">
            <q-spinner color="primary" size="40px" />
          </div>
        </div>

        <div v-if="!this.loading && this.projectTypes.length === 0" class="empty-state">
          <q-icon name="inbox" size="48px" color="grey-5" />
        </div>

        <div v-else class="project-types-list">
          <div v-for="projectType in this.projectTypes" :key="projectType.project_type_id" class="project-type-item"
            :class="{ 'is-checked': projectType.is_checked }" @click="onItemClick(projectType)">
            <CustomCheckbox :label="projectType.project_type_name" :value="projectType.is_checked"
              @change="(value) => onProjectTypeChange(projectType, value)" />
          </div>
        </div>
      </div>
      <CustomModalFooter :btn-loading="loading && isBulk" :hide-submit-btn="!isBulk" cancel-btn-text="Закрыть"
        submit-btn-text="Сохранить" :disable-submit="isBulk && selectedProjectTypeIds.length === 0"
        :on-submit="handleBulkSubmit" />
    </q-form>
  </div>
</template>
<script>

import CustomCheckbox from "@/components/form/CustomCheckbox";
import CustomModalFooter from "@/components/modal/modals/modal-footer";
import { getParamTypes, updateProjectLink, deleteProjectLink, bulkUpdateProjectLink } from "@/views/FloorParams/services";
import { useAppStore } from "@/services/store/app";
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";

export default {
  name: "add-param-link",
  components: { CustomModalFooter, CustomCheckbox },
  props: ['data'],
  data() {
    return {
      loading: false,
      projectTypes: [],
      floor_param_type_id: null,
      isBulk: false,
      selectedProjectTypeIds: [],
      selectedFloorParamTypeIds: [],
      rows: [],
    }
  },
  methods: {
    handleFormSubmit() {
      if (this.isBulk) {
        this.handleBulkSubmit();
      }
    },
    async getData(showLoading = true) {
      if (!this.floor_param_type_id) return;
      if (showLoading) {
        this.loading = true;
      }
      const res = await getParamTypes(this.floor_param_type_id);
      if (showLoading) {
        this.loading = false;
      }
      if (!res) return;
      const projectTypes = res || [];
      this.projectTypes = projectTypes.map((item) => ({
        ...item,
        is_checked: this.isBulk ? false : item.is_checked,
      }));
    },
    async reloadData() {
      await this.getData(false);
    },
    onItemClick(projectType) {
      if (this.loading) return;
      const newValue = !projectType.is_checked;
      this.onProjectTypeChange(projectType, newValue);
    },
    async onProjectTypeChange(projectType, value) {
      if (this.loading) return;
      if (this.isBulk) {
        projectType.is_checked = value;
        if (value) {
          if (!this.selectedProjectTypeIds.includes(projectType.project_type_id)) {
            this.selectedProjectTypeIds.push(projectType.project_type_id);
          }
        } else {
          this.selectedProjectTypeIds = this.selectedProjectTypeIds.filter(
            (id) => id !== projectType.project_type_id
          );
        }
        return;
      }
      if (!this.floor_param_type_id) return;

      const isCurrentlyLinked = projectType.floor_param_project_link_id !== null;
      const shouldBeLinked = value;

      if (isCurrentlyLinked && !shouldBeLinked) {
        this.loading = true;
        try {
          await deleteProjectLink(projectType.floor_param_project_link_id);
          await this.reloadData();
          if (this.data?.onSubmit) {
            await this.data.onSubmit();
          }
        } finally {
          this.loading = false;
        }
      } else if (!isCurrentlyLinked && shouldBeLinked) {
        this.loading = true;
        try {
          await updateProjectLink({
            floor_param_project_link_id: null,
            floor_param_type_id: this.floor_param_type_id,
            project_type_id: projectType.project_type_id,
          });
          await this.reloadData();
          if (this.data?.onSubmit) {
            await this.data.onSubmit();
          }
        } finally {
          this.loading = false;
        }
      }
    },
    async handleBulkSubmit() {
      if (!this.isBulk) return;
      if (!this.selectedProjectTypeIds.length || !this.selectedFloorParamTypeIds.length) {
        this.showErrorSnackbar('Выберите хотя бы один тип проекта');
        return;
      }
      this.loading = true;
      const res = await bulkUpdateProjectLink({
        project_type_ids: this.selectedProjectTypeIds,
        floor_param_type_ids: this.selectedFloorParamTypeIds,
      });
      this.loading = false;
      if (!res) return;
      await this.data.onSubmit();
      this.showSucceedSnackbar('Привязки обновлены');
      this.closeModal();
    },
  },
  mounted() {
    const rowsPayload = this.data?.body?.rows;
    if (Array.isArray(rowsPayload) && rowsPayload.length > 0) {
      this.rows = rowsPayload;
      this.isBulk = !!this.data?.body?.isBulk && rowsPayload.length > 1;
      this.selectedFloorParamTypeIds = rowsPayload
        .map((row) => row?.floor_param_type_id)
        .filter(Boolean);
    }
    const floorParamTypeId =
      this.data?.body?.floor_param_type_id ||
      rowsPayload?.[0]?.floor_param_type_id ||
      this.data?.floor_param_type_id;
    if (floorParamTypeId && !this.floor_param_type_id) {
      this.floor_param_type_id = floorParamTypeId;
      this.getData();
    }
  },
  setup() {
    const { closeModal } = useAppStore();
    const { showSucceedSnackbar, showErrorSnackbar } = useCustomSnackbar();
    return {
      showSucceedSnackbar,
      showErrorSnackbar,
      closeModal,
    };
  },
}
</script>

<style scoped lang="scss">
.modal-wrapper {
  position: relative;
}

.form {
  display: flex;
  flex-direction: column;
  width: 480px;
  gap: 16px;
  position: relative;

  @media (max-width: 540px) {
    width: 100%;
  }

  &__item {
    position: relative;
    min-height: 200px;
  }
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(3px);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  margin: 0;
  padding: 0;

  .loading-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0;
    margin: 0;
    padding: 0;
  }
}

.empty-state {
  padding: 40px 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;

  .empty-text {
    color: #9e9e9e;
    font-size: 14px;
  }
}

.project-types-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 380px;
  overflow-y: auto;
  padding: 4px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f5f5f5;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #1976d2;
    border-radius: 3px;

    &:hover {
      background: #1565c0;
    }
  }
}

.project-type-item {
  padding: 10px 14px;
  border-radius: 8px;
  background: #ffffff;
  border: 1.5px solid #e0e0e0;
  transition: all 0.2s ease;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  &:hover {
    background: #f8f9fa;
    border-color: #bdbdbd;
    transform: translateX(2px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  &.is-checked {
    background: #e3f2fd;
    border-color: #1976d2;
    box-shadow: 0 2px 8px rgba(25, 118, 210, 0.15);

    &:hover {
      background: #bbdefb;
      border-color: #1565c0;
    }

    .check-icon {
      color: #1976d2;
      font-size: 20px;
      animation: scaleIn 0.2s ease;
    }
  }

  .check-icon {
    flex-shrink: 0;
  }
}

@keyframes scaleIn {
  from {
    transform: scale(0);
    opacity: 0;
  }

  to {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
